/**
 * AddAccountPartyModal — Task #411 (2026-06-10, §3g Phase A3).
 *
 * Modal-that-stages for Person↔Account relationships, mirroring
 * AddPolicyPartyModal (#368) in shape and convention. AccountParty has a
 * simpler schema than InsurancePolicyParty (just role + optional notes —
 * no designation / percentage_share / designation_date), so this modal
 * is a slimmed-down sibling.
 *
 * The modal returns the built payload + the resolved Person display name
 * to the parent via `onStage(payload, personDisplayName)`. The parent
 * appends a CREATE op (with a freshly-minted tempId) to its pendingOps
 * queue and commits the whole batch through maker-checker when the user
 * clicks Save Changes on the AccountDetail page.
 *
 * Per the app-wide convention referenced in AddPolicyPartyModal lines
 * 140-143 (#101 won't-do — picker excludeIds): we do NOT filter the
 * picker against already-assigned Person IDs. Duplicates are caught by
 * the backend's 409 on Save and surfaced as a Failed marker on the
 * pending row, with friendly retry on the parent page. This deviates
 * from the original #411 task spec which had specified an
 * `existingPersonIds` prop — recorded in the #411 closure footer.
 *
 * P18 (team feedback fb 33, 2026-10-10): opens on the same Create new person /
 * Attach existing person chooser as the Agreement's Add-party modal
 * (PartyAddSteps), including the create-person round-trip resume.
 */
import {
  Alert, Button, Group, Modal, Select, Stack, Text, Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { PersonPicker } from './EntityPicker';
import {
  PartyAddChooser, PartyCreatePersonStep, newPartyPersonUrl, usePartyAddStep,
} from './PartyAddSteps';
import { usePerson } from '../hooks/usePersons';
import type { AccountPartyCreateInput, AccountPartyRole, UUID } from '../api/types';

const ROLE_OPTS: { value: AccountPartyRole; label: string }[] = [
  { value: 'JOINT_HOLDER', label: 'Joint Holder' },
  { value: 'INSURANCE_BENEFICIARY', label: 'Insurance Beneficiary' },
];

interface FormValues {
  person_id: string;
  role: AccountPartyRole;
  notes: string;
}

const EMPTY: FormValues = {
  person_id: '',
  role: 'JOINT_HOLDER',
  notes: '',
};

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Account this party will be added to. Displayed read-only at the top
   *  of the modal for confirmation; used to build the payload. */
  accountId: UUID;
  accountLabel: string;
  /** Called with the built payload + the resolved Person display name
   *  (for friendly rendering on the pending row before the row exists in
   *  the DB). The parent appends a CREATE op to its pendingOps queue
   *  with a freshly-minted tempId and dismisses the modal. */
  onStage: (payload: AccountPartyCreateInput, personDisplayName: string | null) => void;
  /** P18: when set, open straight on the Attach step with this person
   *  pre-selected (the resume leg of the "Create new person" round-trip). */
  initialPersonId?: string | null;
}

export function AddAccountPartyModal({
  opened, onClose, accountId, accountLabel, onStage, initialPersonId,
}: Props) {
  const form = useForm<FormValues>({
    initialValues: EMPTY,
    validate: {
      person_id: (v) => (v.length >= 32 ? null : 'Person required'),
    },
  });

  // Clean slate on every open (dismiss = discard, §3g); a resumed create
  // round-trip opens on 'attach' with the new person pre-selected.
  const [step, setStep] = usePartyAddStep(
    opened, initialPersonId, (pid) => form.setValues({ ...EMPTY, person_id: pid }),
  );

  // The account page keeps its tab in local state, not the URL, so the return
  // URL is the bare account; the page's resume handler opens the Parties tab.
  const navigate = useNavigate();
  function handleCreateNew() {
    onClose();
    navigate(newPartyPersonUrl(`/accounts/${accountId}`));
  }

  // Resolve the picked Person's display name so the pending row renders
  // "Andre Pereira Alves" rather than an 8-char UUID prefix. Disabled
  // until person_id has a real value to avoid an enabled+empty fetch.
  const personQ = usePerson(form.values.person_id || undefined);
  const personDisplayName = personQ.data?.display_name ?? null;

  function handleSave(values: FormValues) {
    const payload: AccountPartyCreateInput = {
      account_id: accountId,
      person_id: values.person_id,
      role: values.role,
    };
    if (values.notes) payload.notes = values.notes;
    onStage(payload, personDisplayName);
    onClose();
  }

  // C-113 guard (2026-06-10, surfaced by A4 smoke Test 1 on the Agreement
  // page): the modal renders within the page's <form> React subtree, so
  // without stopPropagation the inner submit ALSO fires the page's Save.
  // On AccountDetailPage the page Save happened to no-op when nothing was
  // dirty (which is why A3's smoke didn't catch it), but the latent bug
  // was real — e.g. a dirty field + staging would double-fire. Same fix
  // as InteractionFormModal (#157).
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    form.onSubmit(handleSave)(e);
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Add person to this account" size="lg">
      {step === 'choose' && (
        <PartyAddChooser recordLabel={accountLabel} recordNoun="account"
          onPick={setStep} onCancel={onClose} />
      )}

      {step === 'create' && (
        <PartyCreatePersonStep onBack={() => setStep('choose')} onOpenForm={handleCreateNew} />
      )}

      {step === 'attach' && (
      <form onSubmit={handleFormSubmit}>
        <Stack>
          <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm" fw={500}>{accountLabel}</Text>
            <Text size="xs" c="dimmed">
              The new party row is staged and only commits when you click
              Save Changes on the account page.
            </Text>
          </Alert>

          <PersonPicker
            label="Person"
            description="Pick the person to attach in the selected role."
            value={form.values.person_id || null}
            onChange={(v) => form.setFieldValue('person_id', v ?? '')}
            error={form.errors.person_id as string | undefined}
          />

          <Select
            label="Role"
            data={ROLE_OPTS}
            value={form.values.role}
            onChange={(v) => v && form.setFieldValue('role', v as AccountPartyRole)}
            allowDeselect={false}
            required
          />

          <Textarea
            label="Notes"
            placeholder="Optional"
            value={form.values.notes}
            onChange={(e) => form.setFieldValue('notes', e.currentTarget.value)}
            autosize
            minRows={2}
          />

          <Group justify="space-between" gap="xs">
            <Button variant="default" onClick={() => setStep('choose')}>Back</Button>
            <Button type="submit">Stage party</Button>
          </Group>
        </Stack>
      </form>
      )}
    </Modal>
  );
}
